import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchCurrentlyPlaying } from "../utils/fetchCurrentlyPlaying"; // Import the utility function
import TextareaAutosize from "react-textarea-autosize";
import SaveConfirmationModal from "./SaveConfirmationModal";
import { BeatLoader } from "react-spinners";
import PlaylistCreatedModal from "./PlaylistCreatedModal";
import PlaylistTitleModal from "./PlaylistTitleModal";

const Note = () => {
  const { noteId } = useParams(); // Get the note ID from the URL
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true); // State to manage loading
  const navigate = useNavigate(); // Hook to navigate after saving
  const [currentlyPlayingTrack, setCurrentlyPlayingTrack] = useState(null); // Local state for currently playing track
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [lastPlayedTrack, setLastPlayedTrack] = useState(null); // State to track the last played track
  const [localChanges, setLocalChanges] = useState({
    songs: [],
    content: "",
  });
  const apiUrl = process.env.REACT_APP_FLASK_API;
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [playlistUrl, setPlaylistUrl] = useState(null);
  const [showPlaylistTitleModal, setShowPlaylistTitleModal] = useState(false);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        const response = await fetch(`${apiUrl}/get_note/${noteId}`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          console.log(response);
          throw new Error("Failed to fetch note");
        }
        const noteData = await response.json();
        setNote(noteData);
        setLocalChanges({
          songs: noteData.songs || [],
          content: noteData.content,
        });
      } catch (error) {
        console.log(error);
        console.error("Error fetching note:", error);
        setNote(null); // Set note to null if there's an error
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [noteId]);

  useEffect(() => {
    const getCurrentlyPlaying = async () => {
      const trackData = await fetchCurrentlyPlaying(); // Call the utility function
      if (trackData && trackData.name && trackData.name !== lastPlayedTrack) {
        setCurrentlyPlayingTrack(trackData); // Update local state with fetched track data
        // Call storeCurrentlyPlaying directly after fetching the track
        await storeCurrentlyPlaying(trackData);
      }
    };

    // Fetch the currently playing track immediately
    getCurrentlyPlaying();

    // Set up an interval to fetch the currently playing track every 5 seconds
    const interval = setInterval(getCurrentlyPlaying, 5000); // 5000 milliseconds = 5 seconds

    // Cleanup function to clear the interval if the component unmounts
    return () => clearInterval(interval);
  }, [note, lastPlayedTrack]); // Add note and lastPlayedTrack to dependencies

  const storeCurrentlyPlaying = async (trackData) => {
    if (!trackData || !note) {
      console.log("No track data or note available", { trackData, note });
      return;
    }

    // Check if the song already exists in the note's songs array
    const songExists = localChanges.songs.some(
      (song) => song.song_id === trackData.id
    );

    // Check if this is the same track as last time
    const isSameTrack = lastPlayedTrack === trackData.name;

    if (!songExists && !isSameTrack) {
      // Post the new song to the note
      const response = await fetch(`${apiUrl}/add_song_to_note/${noteId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          song_id: trackData.id,
          title: trackData.name,
          artist: trackData.artists[0],
          album_image_url: trackData.album_image,
          album: trackData.album,
        }),
      });

      if (response.status !== 201) {
        console.error("Failed to add song to note:", await response.text());
      } else {
        console.log("Song added to note successfully!");

        // Update the note's songs array
        setLocalChanges((prev) => ({
          ...prev,
          songs: [
            ...prev.songs,
            {
              song_id: trackData.id,
              title: trackData.name,
              artist: trackData.artists[0],
              album_image_url: trackData.album_image,
              album: trackData.album,
            },
          ],
        }));
      }
      setLastPlayedTrack(trackData.name); // Update last played track
    }
  };

  const handleChange = (event) => {
    setLocalChanges((prev) => ({
      ...prev,
      content: event.target.value,
    }));
  };

  const handleBack = async () => {
    if (
      localChanges.content !== note.content ||
      localChanges.songs.length !== note.songs.length
    ) {
      setShowSaveModal(true);
    } else {
      navigate("/home");
    }
  };

  const handleSaveAndClose = async () => {
    try {
      await handleSave();
      setShowSaveModal(false);
      navigate("/home");
    } catch (error) {
      console.error("Error saving and closing:", error);
    }
  };

  const handleSave = async () => {
    try {
      // Save content changes
      if (localChanges.content !== note.content) {
        const contentResponse = await fetch(`${apiUrl}/update_note/${noteId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: localChanges.content }),
          credentials: "include",
        });

        if (!contentResponse.ok) {
          throw new Error("Failed to update note content");
        }
      }

      // Save song changes
      if (localChanges.songs.length > note.songs.length) {
        const newSongs = localChanges.songs.slice(note.songs.length);
        for (const song of newSongs) {
          const songResponse = await fetch(
            `${apiUrl}/add_song_to_note/${noteId}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                song_id: song.song_id,
                title: song.title,
                artist: song.artist,
                album_image_url: song.album_image_url,
                album: song.album,
              }),
            }
          );

          if (!songResponse.ok) {
            throw new Error("Failed to add song to note");
          }
        }
      }

      // Update the note state with local changes
      setNote((prev) => ({
        ...prev,
        content: localChanges.content,
        songs: localChanges.songs,
      }));

      // Only navigate after all saves are successful
      return true;
    } catch (error) {
      console.error("Error saving note:", error);
      throw error; // Re-throw the error to be handled by the calling function
    }
  };

  const handleDiscard = () => {
    setShowSaveModal(false);
    navigate("/home");
  };

  const handleCancel = () => {
    setShowSaveModal(false);
  };

  const createPlaylist = async (noteId, title) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FLASK_API}/create_playlist/${noteId}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ title }),
        }
      );

      if (!response.ok) {
        console.log(response);
        throw new Error("Failed to create playlist");
      }

      const data = await response.json();
      setPlaylistUrl(data.playlist_url);
      setShowPlaylistModal(true);
    } catch (error) {
      console.error("Error creating playlist:", error);
      alert("Failed to create playlist. Please try again.");
    }
  };

  const handlePlaylistCreateClick = () => {
    setShowPlaylistTitleModal(true);
  };

  const handlePlaylistTitleSubmit = (title) => {
    setShowPlaylistTitleModal(false);
    createPlaylist(noteId, title);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <BeatLoader
          color="#c084fc"
          size={50}
        />
      </div>
    ); // Show loading state
  }

  if (!note) {
    return <div>Note not found!</div>; // Handle case where note is not found
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white py-8">
      <div className="max-w-6xl mx-auto px-4 h-[90vh] flex flex-col">
        {/* Back button at the top */}
        <button
          onClick={handleBack}
          className="text-purple-400 hover:text-purple-300 mb-4 flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-purple-600/10 hover:bg-purple-700/10 rounded-lg transition-colors text-sm lg:text-base whitespace-nowrap"
        >
          <svg
            className="w-4 h-4 lg:w-5 lg:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span>Back</span>
        </button>

        {/* Currently Playing Section */}
        {currentlyPlayingTrack && (
          <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h3 className="text-lg font-semibold mb-2 text-purple-400">
              Currently Playing
            </h3>
            <div className="flex items-center">
              <img
                src={currentlyPlayingTrack.album_image}
                alt={currentlyPlayingTrack.name}
                className="w-12 h-12 rounded mr-3"
              />
              <div>
                <p className="font-bold">{currentlyPlayingTrack.name}</p>
                <p className="text-gray-400">
                  {currentlyPlayingTrack.artists[0]}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main content grid - updated for mobile responsiveness */}
        <div className="flex flex-col md:flex-row gap-8 mb-8 flex-1">
          {/* Note Content Section - now comes first on mobile */}
          <div className="w-full md:w-2/3 h-full flex flex-col order-1 md:order-2">
            <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700 shadow-xl flex-1 flex flex-col">
              <h2 className="text-xl font-semibold mb-4 text-purple-400">
                Note
              </h2>
              <div className="flex-1 overflow-y-auto">
                <TextareaAutosize
                  style={{
                    boxSizing: "border-box",
                    backgroundColor: "rgba(55, 65, 81, 0.5)",
                  }}
                  minRows={5}
                  maxRows={50}
                  value={localChanges.content}
                  onChange={handleChange}
                  className="w-full h-full rounded-lg p-3 text-white focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Songs Section - now comes second on mobile */}
          <div className="w-full md:w-1/3 h-full flex flex-col order-2 md:order-1">
            <div className="bg-gray-800/50 backdrop-blur-lg rounded-2xl p-6 border border-gray-700 shadow-xl flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-purple-400">Songs</h2>
                {localChanges.songs.length > 0 && (
                  <button
                    onClick={handlePlaylistCreateClick}
                    className="flex items-center gap-2 px-3 py-1.5 lg:px-4 lg:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm lg:text-base whitespace-nowrap"
                  >
                    <svg
                      className="w-4 h-4 lg:w-5 lg:h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span>Create Playlist</span>
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {localChanges.songs.length > 0 ? (
                  [...localChanges.songs].reverse().map((song) => (
                    <div
                      key={song.song_id}
                      className="flex items-center mb-2"
                    >
                      <img
                        src={song.album_image_url}
                        alt={song.title}
                        className="w-12 h-12 rounded mr-3"
                      />
                      <div>
                        <h3 className="font-bold">{song.title}</h3>
                        <p className="text-gray-600">{song.artist}</p>
                        <p className="text-gray-500">{song.album}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No songs available.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSaveModal && (
        <SaveConfirmationModal
          onSave={handleSaveAndClose}
          onDiscard={handleDiscard}
          onCancel={handleCancel}
        />
      )}

      {showPlaylistModal && (
        <PlaylistCreatedModal
          playlistUrl={playlistUrl}
          onClose={() => setShowPlaylistModal(false)}
        />
      )}

      {showPlaylistTitleModal && (
        <PlaylistTitleModal
          onSubmit={handlePlaylistTitleSubmit}
          onClose={() => setShowPlaylistTitleModal(false)}
        />
      )}
    </div>
  );
};

export default Note;
